import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Universal CSV/XLSX importer for any entity ledger.
 * Surfaces backend duplicate-detection counts in the success toast.
 */
export function EntityImporter({ config, open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const close = (v) => {
    onOpenChange(v);
    if (!v) setFile(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/entities/${config.apiPath}/import`, fd);
      const dup = data.skipped_duplicates || 0;
      toast.success(
        dup
          ? `Imported ${data.inserted} rows · ${dup} duplicate${dup > 1 ? "s" : ""} skipped`
          : `Imported ${data.inserted} rows`
      );
      close(false);
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Import {config.title}</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file. Column headers should match the fields (e.g.{" "}
            {config.fields.map((f) => f.name).slice(0, 4).join(", ")}…). Duplicate rows are
            detected automatically and skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="import-file-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button onClick={upload} disabled={!file || uploading} data-testid="upload-file-btn">
            {uploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
